/**
 * Behavior Graph — FlowGraph-based interactivity system.
 *
 * Based on KHR_interactivity (Khronos draft) + Trace extensions.
 * See docs-internal/BEHAVIOR_GRAPH_SPEC.md for full specification.
 */

import { BehaviorData } from '../behaviordata';

// ============================================================================
// Op constants — KHR_interactivity standard + Trace extensions
// ============================================================================

/** KHR_interactivity standard ops (domain/operation naming) */
export const GraphOps = {
    // Events
    EVENT_ON_START: 'event/onStart',
    EVENT_ON_TICK: 'event/onTick',
    /** Send a custom event (`configuration.event` = index into `GraphData.events`). */
    EVENT_SEND: 'event/send',
    /** Entry node activated when its custom event is sent (`configuration.event` = index). */
    EVENT_RECEIVE: 'event/receive',

    // Flow control
    FLOW_BRANCH: 'flow/branch',
    FLOW_SEQUENCE: 'flow/sequence',
    FLOW_SET_DELAY: 'flow/setDelay',
    FLOW_CANCEL_DELAY: 'flow/cancelDelay',

    // Math: arithmetic
    MATH_ADD: 'math/add',
    MATH_SUB: 'math/sub',
    MATH_MUL: 'math/mul',
    MATH_DIV: 'math/div',
    MATH_MIN: 'math/min',
    MATH_MAX: 'math/max',
    MATH_POW: 'math/pow',
    MATH_FLOOR: 'math/floor',
    MATH_RANDOM: 'math/random',

    // Math: comparison
    MATH_EQ: 'math/eq',
    MATH_GT: 'math/gt',
    MATH_GE: 'math/ge',
    MATH_LT: 'math/lt',
    MATH_LE: 'math/le',

    // Math: boolean logic (spec reuses math/ for boolean ops)
    MATH_NOT: 'math/not',
    MATH_AND: 'math/and',
    MATH_OR: 'math/or',
    MATH_SELECT: 'math/select',
    MATH_COMBINE3: 'math/combine3',

    // Pointer (KHR_interactivity standard — transform read/write)
    POINTER_GET: 'pointer/get',
    POINTER_SET: 'pointer/set',
    /** KHR-standard animated pointer write: value + duration + cubic-bezier p1/p2. */
    POINTER_INTERPOLATE: 'pointer/interpolate',

    // Variables
    VARIABLE_GET: 'variable/get',
    VARIABLE_SET: 'variable/set',

    // KHR interaction events (KHR_node_selectability / KHR_node_hoverability)
    EVENT_ON_SELECT: 'event/onSelect',
    EVENT_ON_HOVER_IN: 'event/onHoverIn',
    EVENT_ON_HOVER_OUT: 'event/onHoverOut',

    // ── Trace extensions (trace/ prefix) ──
    TRACE_ON_PROXIMITY: 'trace/onProximity',
    TRACE_SET_ACTIVE: 'trace/setActive',
    TRACE_GET_ACTIVE: 'trace/getActive',
    TRACE_GET_CHILD_COUNT: 'trace/getChildCount',
    TRACE_SET_ACTIVE_CHILD: 'trace/setActiveChild',
    TRACE_PLAY_ANIMATION: 'trace/playAnimation',
    TRACE_PLAY_ANIMATION_EXTERNAL: 'trace/playAnimationExternal',
    /** @deprecated Use `TRACE_PLAY_MEDIA` (with `play=true`) instead. Still loaded for back-compat. */
    TRACE_PLAY_VIDEO: 'trace/playVideo',

    // Media playback — works on any Playable (Video, AudioClip, Animation, Pages, Timeline).
    // Capability-keyed: `src` only honored when the target implements PlayableSource;
    // `volume` only honored when target implements PlayableVolume.
    TRACE_PLAY_MEDIA: 'trace/playMedia',
    TRACE_PAUSE_MEDIA: 'trace/pauseMedia',
    TRACE_STOP_MEDIA: 'trace/stopMedia',
    TRACE_SEEK_MEDIA: 'trace/seekMedia',
    TRACE_SET_MEDIA_VOLUME: 'trace/setMediaVolume',
    // Data readers: playing state only where the Playable exposes it (Video,
    // AudioClip, Animation…); volume only where a reader exists (Video, AudioClip).
    TRACE_IS_MEDIA_PLAYING: 'trace/isMediaPlaying',
    TRACE_GET_MEDIA_VOLUME: 'trace/getMediaVolume',
    // Runtime on/off switch for RuntimeToggleable behaviors (Billboard, Tether, ViewAnchor)
    TRACE_SET_BEHAVIOR_ENABLED: 'trace/setBehaviorEnabled',
    TRACE_NAVIGATE: 'trace/navigate',
    TRACE_GET_PROPERTY: 'trace/getProperty',
    TRACE_SET_PROPERTY: 'trace/setProperty',
    TRACE_TIME: 'trace/time',
    TRACE_SET_STATE: 'trace/setState',
    TRACE_GET_STATE: 'trace/getState',
    TRACE_ON_STATE_CHANGE: 'trace/onStateChange',

    // Pages
    PAGES_NEXT: 'pages/next',
    PAGES_PREV: 'pages/prev',
    PAGES_GET_ACTIVE_INDEX: 'pages/getActiveIndex',
    PAGES_GET_PAGE_COUNT: 'pages/getPageCount',
} as const;

export type GraphOp = typeof GraphOps[keyof typeof GraphOps];

// ============================================================================
// Graph primitives
// ============================================================================

/** KHR type signature + TRACE_interactivity type extensions (string, color) */
export type TypeSignature = 'bool' | 'int' | 'float' | 'float2' | 'float3' | 'float4'
    | 'float2x2' | 'float3x3' | 'float4x4'
    | 'string'   // TRACE_interactivity extension — text, URLs, property names
    | 'color';   // TRACE_interactivity extension — #rrggbb hex string

export interface TypeDefinition {
    signature: TypeSignature;
}

export interface Declaration {
    /** Namespaced operation type, e.g. "math/add", "trace/setActive" */
    op: string;
    /**
     * TRACE_subgraph extension — when present, this declaration's op is
     * implemented by the referenced subgraph rather than a built-in block.
     */
    extensions?: {
        TRACE_subgraph?: SubgraphRef;
    };
}

/** Reference to a subgraph implementation */
export interface SubgraphRef {
    /** Index into the containing file's graphs[] array (or the asset's graphs[]) */
    graph: number;
    /** Index into glXF assets[] array. Absent for internal (same-file) subgraphs. */
    asset?: number;
}

export interface GraphVariable {
    /** Variable name (for editor UI — resolution is by index) */
    name: string;
    /** Index into types[] */
    type: number;
    /**
     * Initial value as flat component array
     * @default []
     */
    value: unknown[];
}

export interface GraphEvent {
    /**
     * External event identifier. Empty string = an INTERNAL event (the KHR
     * spec's event without an `id`): it only links `event/send` to
     * `event/receive` nodes of the same graph — the way repetition (e.g. the
     * Interval macro) re-enters an acyclic graph. Nodes address events by
     * INDEX (`configuration.event`), never by id.
     */
    id: string;
    /** Optional typed parameters */
    values?: Record<string, { type: number; value?: unknown[] }>;
}

// ============================================================================
// Node connections
// ============================================================================

/** A value input: pulled from another node's output, or an inline literal */
export type ValueInput =
    | { node: number; socket: string }
    | { type: number; value: unknown[] };

/** A flow output: points to the next execution node */
export interface FlowTarget {
    node: number;
    socket: string;
}

/** Static configuration value */
export interface ConfigValue {
    type: number;
    value: unknown[];
}

// ============================================================================
// Graph node
// ============================================================================

export interface GraphNode {
    /** Index into declarations[] */
    declaration: number;
    /** Static configuration (target paths, clip names, variable indices) */
    configuration?: Record<string, ConfigValue>;
    /** Value input sockets — data dependencies */
    values?: Record<string, ValueInput>;
    /** Flow output sockets — execution routing */
    flows?: Record<string, FlowTarget>;
    /** Editor metadata (position, etc.) — ignored by runtime */
    metadata?: Record<string, unknown>;
}

// ============================================================================
// Subgraph interface — defines a graph's input/output contract
// ============================================================================

/** Maps a named flow socket to the internal node+socket that handles it */
export interface FlowSocketMapping {
    node: number;
    socket: string;
}

/** Declares a named value socket with its type */
export interface ValueSocketDeclaration {
    /** Index into this graph's types[] array */
    type: number;
    /** Internal node index that consumes this input (editor only) */
    node?: number;
    /** Socket name on the internal node (editor only, defaults to the socket key name) */
    socket?: string;
    /** Human-readable description (editor only) */
    description?: string;
}

/** Declares a named output value socket — points to the internal node that produces the value */
export interface OutputValueSocketDeclaration {
    /** Internal node index that produces this output */
    node: number;
    /** Socket name on the internal node */
    socket: string;
    /** Index into this graph's types[] array */
    type: number;
    /** Human-readable description (editor only) */
    description?: string;
}

// ============================================================================
// Top-level graph
// ============================================================================

export interface GraphData {
    /**
     * Type definitions referenced by index
     * @default []
     */
    types: TypeDefinition[];
    /**
     * Node operation declarations referenced by index
     * @default []
     */
    declarations: Declaration[];
    /**
     * Graph-scoped mutable variables
     * @default []
     */
    variables: GraphVariable[];
    /**
     * Custom event definitions
     * @default []
     */
    events: GraphEvent[];
    /**
     * The nodes
     * @default []
     */
    nodes: GraphNode[];

    // ── Subgraph interface (present only when this graph is used as a subgraph) ──

    /** Named flow entry points — maps socket name → internal node+socket */
    inputFlowSockets?: Record<string, FlowSocketMapping>;
    /** Named flow exit points — maps socket name → internal node+socket */
    outputFlowSockets?: Record<string, FlowSocketMapping>;
    /** Named value inputs with types — the parent supplies these */
    inputValueSockets?: Record<string, ValueSocketDeclaration>;
    /** Named value outputs — points to internal node+socket that produces the value */
    outputValueSockets?: Record<string, OutputValueSocketDeclaration>;
}

// ============================================================================
// Behavior data
// ============================================================================

export interface BehaviorGraphData extends BehaviorData {
    '@type': 'BehaviorGraph';

    /**
     * The behavior graph definition (single-graph mode, backward compat).
     * When `graphs` is present, this field is ignored.
     * @default {"types":[],"declarations":[],"variables":[],"events":[],"nodes":[]}
     */
    graph?: GraphData;

    /**
     * Multiple graph definitions (TRACE_subgraph mode).
     * graphs[0] is typically the main graph; other entries are subgraph definitions.
     * The active (entry-point) graph index is specified by `activeGraph`.
     */
    graphs?: GraphData[];

    /**
     * Index into `graphs[]` for the entry-point graph. Defaults to 0.
     * @default 0
     */
    activeGraph?: number;
}
